import { call, put, select, takeLatest } from 'redux-saga/effects'
import { FillablePDFsConstants } from '../store/fillablePDFs.constants'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getServiceIdSelector } from '../../user/selectors'
import { getPcrIdSelector } from '../../home/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import type { IPcr } from '../../pcr/store/pcr.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setCustomInputs } from '../store/fillablePDFs.actions'
import { inputTypeLookup, ICustomInput } from '../store/fillablePDFs.types'
import type { FormPage, FormSection } from '../../../components/form/Form.types'
import { getFillablePDFOptionsSelector } from '../../options/selectors'
import { IFillablePDFOptionsState } from '../../options/store/options.types'
import { IBindingOption, IBindingOptions } from '../../../common/common.types'

const resolvePcrLocation = (obj: any, bindingOption: IBindingOption) => {
  if (!obj || !bindingOption) return undefined

  let value = getValueForPath(obj, bindingOption.appStateLocationPNNV)
  if (value) {
    return value
  }

  value = getValueForPath(obj, bindingOption.appStateLocation)
  if (value) {
    return value
  }

  return null
}

const getValueForPath = (obj: any, path: string) => {
  if (!obj || !path) return undefined

  const tokens = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)

  return tokens.reduce((acc, key) => acc?.[key], obj)
}

const isEmpty = (v: any) => v == null || v === '' || (Array.isArray(v) && v.length === 0)

const buildBindingByUuid = (bindings: IBindingOptions | undefined | null): Record<string, IBindingOption> => {
  const map: Record<string, IBindingOption> = {}
  ;(bindings ?? []).forEach((b) => {
    if (!b?.uuid || !b?.appStateLocation) return
    map[b.uuid] = b
  })
  return map
}

export function* hydrateFillablePdfFromPcrSagaWorker(action: any) {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const fillablePDFOptionsState: IFillablePDFOptionsState = yield select(getFillablePDFOptionsSelector)
  const { bindings } = fillablePDFOptionsState

  if (!pcrID) return

  const fillablePDF = action.fillablePDF
  if (!fillablePDF) return

  const bindingByUuid = buildBindingByUuid(bindings)

  const pcrDocsService = new PcrDocsService(serviceId)

  try {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

    const inputs: ICustomInput[] = []

    const elements =
      fillablePDF.pages?.flatMap((p: FormPage) => p.sections ?? []).flatMap((s: FormSection) => s.elements ?? []) ?? []

    elements
      .filter((e: any) => e.binding)
      .forEach((element: any) => {
        const uuid: string = element.binding
        const bindingOption = bindingByUuid[uuid]
        if (!bindingOption) return

        const value = resolvePcrLocation(pcrDoc, bindingOption)
        if (isEmpty(value)) return

        const fieldType = inputTypeLookup[element.componentInterface] ?? 'text'

        if (Array.isArray(value)) {
          value.forEach((v) =>
            inputs.push({
              questionId: element.id.toString(),
              question: element.label,
              answerId: v,
              fieldType,
              isDisabled: true,
            })
          )
          return
        }

        inputs.push({
          questionId: element.id.toString(),
          question: element.label,
          answerId: value,
          fieldType,
          isDisabled: true,
        })
      })

    yield put(setCustomInputs(inputs))
  } catch (e) {
    yield put(throwPcrException(`hydrateFillablePdfFromPcr() error: ${e}`))
  }
}

export function* hydrateFillablePdfFromPcrSaga() {
  yield takeLatest(
    [FillablePDFsConstants.HYDRATE_FILLABLE_PDF_FROM_PCR],
    performWithMutexHOG,
    hydrateFillablePdfFromPcrSagaWorker
  )
}
